import React, { useMemo, useState, useRef, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Button, DatePicker, Form, Input, Table, TableProps } from 'antd';
import { useGetRoomAvailability } from 'hooks/useGetRoomAvailability';
import { toUtcTime } from 'utils';
import { getRangeDate, setRangeDate, clearRangeDate } from 'utils/dateRangeStorage';
import 'styles/room-availability.scss';
import ConfirmCheckRoomAvailable from 'components/modal/room-available-components/confirm-check-room-available';
import useModal from 'stores/useModal';
import { FormProps } from 'rc-field-form';
import set from 'lodash/set';
import { useTranslation } from 'react-i18next';

const INITIAL_RANGE_DATE = 14;
const initStartDate = dayjs().startOf('day');

function RoomAvailability() {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [selectedDateRange, setSelectedDateRange] = useState<any>([
    initStartDate,
    initStartDate.add(INITIAL_RANGE_DATE, 'day')
  ]);

  const isFirstRender = useRef(true);

  const [filterOptions, setFilterOptions] = useState<any>(() => {
    const savedRange = getRangeDate();
    if (savedRange) {
      const startDate = dayjs(savedRange.startDate);
      return {
        start_date: toUtcTime(startDate.add(1, 'day'), 'YYYY-MM-DD'),
        period: savedRange.period
      };
    }
    return {
      start_date: toUtcTime(initStartDate.add(1, 'day'), 'YYYY-MM-DD'),
      period: INITIAL_RANGE_DATE
    };
  });

  const { isOpenCheckRoomAvailability, setIsOpenCheckRoomAvailability } = useModal();
  const [dateToQuery, setDateToQuery] = useState<Dayjs | undefined>();
  const [roomTypeToQuery, setRoomTypeToQuery] = useState<string>('');
  const [roomTypeIdToQuery, setRoomTypeIdToQuery] = useState<number>(0);

  const { data: roomAvailability, isFetching } = useGetRoomAvailability(filterOptions);

  const onHandleCheckRoomAvailable = (date?: Dayjs, roomType?: string, roomTypeId?: number) => {
    setIsOpenCheckRoomAvailability(true);
    setDateToQuery(date);
    setRoomTypeToQuery(roomType ?? '');
    setRoomTypeIdToQuery(roomTypeId ?? 0);
  };

  const onCancelCheckRoomAvailable = () => {
    setIsOpenCheckRoomAvailability(false);
  };

  const onHandleClose = () => {
    setIsOpenCheckRoomAvailability(false);
  };

  const onFinish: FormProps['onFinish'] = async (objValue: any) => {
    const truthyValues: any = Object.keys(objValue)
      .filter(key => Boolean(objValue[key]))
      .reduce((cur, next) => {
        return {
          ...cur,
          [next]: objValue[next]
        };
      }, {});

    const { rangeDate, period, ...restFilterOptions } = truthyValues;
    if (rangeDate) {
      const startDate: Dayjs = rangeDate[0];
      const endDate: Dayjs = rangeDate[1];

      if (startDate && endDate) {
        const dayCount = endDate.diff(startDate, 'day');
        set(restFilterOptions, 'period', dayCount + 1);
        set(restFilterOptions, 'start_date', toUtcTime(startDate.add(1, 'day'), 'YYYY-MM-DD'));
        setRangeDate(startDate, endDate, dayCount + 1);
        setSelectedDateRange([startDate, endDate]);
      }
    }

    set(restFilterOptions, 'period', period || INITIAL_RANGE_DATE);

    setFilterOptions(restFilterOptions);
  };

  const handleRangeDateChange = (dates: any) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      const dayCount = end.diff(start, 'day');
      form.setFieldsValue({ period: dayCount + 1 });
      setRangeDate(start, end, dayCount + 1);
    }
  };

  const handlePeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const period = parseInt(e.target.value);
    if (!isNaN(period)) {
      const startDate = dayjs(form.getFieldValue('rangeDate')[0]);
      const endDate = startDate.add(period - 1, 'day');
      form.setFieldsValue({ rangeDate: [startDate, endDate] });
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      const savedRange = getRangeDate();
      if (savedRange) {
        const startDate = dayjs(savedRange.startDate);
        const endDate = dayjs(savedRange.endDate);
        form.setFieldsValue({
          rangeDate: [startDate, endDate],
          period: savedRange.period
        });
        setSelectedDateRange([startDate, endDate]);
      } else {
        const endDate = initStartDate.add(INITIAL_RANGE_DATE - 1, 'day');
        form.setFieldsValue({
          rangeDate: [initStartDate, endDate],
          period: INITIAL_RANGE_DATE
        });
        setSelectedDateRange([initStartDate, endDate]);
      }
    }
  }, [form]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      clearRangeDate();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const columnCleaningRoom: TableProps<any>['columns'] = useMemo(() => {
    const [startDate, endDate] = selectedDateRange;

    if (!startDate.isValid() || !endDate.isValid()) {
      return [];
    }

    function isClickableCell(roomType: string, cellValue: any, totalRooms: number): boolean {
      const nonClickableTypes = new Set([
        'ALM-ALLOTMENT',
        'Total Availability Rooms',
        'Total Availability Rooms(Not subtracted Allotment)',
        'Total Locked Rooms'
      ]);
      return !nonClickableTypes.has(roomType) && cellValue !== totalRooms;
    }

    let currentStartDate = dayjs(startDate);
    const columns: TableProps<any>['columns'] = [
      {
        title: t('common.bookingData.roomType'),
        dataIndex: 'roomType',
        key: 'roomType',
        fixed: 'left',
        width: 355
      },
      {
        title: t('common.bookingData.roomQty'),
        dataIndex: 'total_rooms',
        key: 'total_rooms',
        fixed: 'left',
        align: 'center',
        width: 135
      }
    ];

    while (!currentStartDate.isSame(endDate.add(1, 'day'), 'day')) {
      const addedColumn = currentStartDate.format('YYYY-MM-DD');
      columns.push({
        align: 'center',
        title: currentStartDate.format('DD/MM'),
        dataIndex: addedColumn,
        key: addedColumn,
        width: 90,
        render: (text: any, record: any) => {
          const isClickable = isClickableCell(record.roomType, text, record.total_rooms);
          return (
            <p className="m-0 text-right" style={{ display: 'flex', justifyContent: 'center' }}>
              {isClickable ? (
                <a
                  onClick={() =>
                    onHandleCheckRoomAvailable(
                      dayjs(addedColumn),
                      record['roomType'],
                      record['product_template_id']
                    )
                  }
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </p>
          );
        },
        onCell: (record: any) => {
          const isClickable = isClickableCell(
            record.roomType,
            record[addedColumn],
            record.total_rooms
          );
          return isClickable
            ? {
                onClick: () =>
                  onHandleCheckRoomAvailable(
                    dayjs(addedColumn),
                    record['roomType'],
                    record['product_template_id']
                  ),
                style: { cursor: 'pointer' }
              }
            : {};
        }
      });
      currentStartDate = currentStartDate.add(1, 'day');
    }
    return columns;
    // eslint-disable-next-line
  }, [selectedDateRange, t]);

  return (
    <div className="pms-room-availability">
      <div className="pms-room-availability__timetable">
        <Form
          form={form}
          initialValues={{
            rangeDate: [initStartDate, initStartDate.add(INITIAL_RANGE_DATE, 'day')],
            period: INITIAL_RANGE_DATE
          }}
          onFinish={onFinish}
          autoComplete="off"
        >
          <div className="pms-room-availability__filter">
            <div className="pms-room-availability__filter__left">
              <Form.Item name="rangeDate">
                <DatePicker.RangePicker
                  className="w-full"
                  placeholder={[t('common.time.fromDate'), t('common.time.toDate')]}
                  format="DD-MM-YYYY"
                  onChange={handleRangeDateChange}
                />
              </Form.Item>
              <Form.Item name="period">
                <Input placeholder={t('common.time.totalDays')} onChange={handlePeriodChange} />
              </Form.Item>
              <Form.Item>
                <Button htmlType="submit" className="ant-btn-secondary btn-submit">
                  {t('common.actions.submitButton')}
                </Button>
              </Form.Item>
            </div>
          </div>
        </Form>
        <div className="pms-room-availability__table">
          <Table
            rowKey="roomType"
            loading={isFetching}
            columns={columnCleaningRoom}
            dataSource={roomAvailability}
            pagination={false}
            scroll={{ x: 1000, y: 'calc(100vh - 260px)' }}
            bordered
            locale={{
              emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
            }}
          />
        </div>
      </div>
      <ConfirmCheckRoomAvailable
        dateToQuery={dateToQuery}
        roomTypeToQuery={roomTypeToQuery}
        roomTypeIdToQuery={roomTypeIdToQuery}
        isOpen={isOpenCheckRoomAvailability}
        onCancel={() => onCancelCheckRoomAvailable()}
        onHandleClose={() => onHandleClose()}
      />
    </div>
  );
}

export default RoomAvailability;
